'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import type { User } from '@/types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (data: { email: string; password: string; firstName: string; lastName: string; referralCode?: string }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  verifyEmail: (token: string) => Promise<void>;
  updateProfile: (data: { firstName?: string; lastName?: string; phone?: string }) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  deleteAccount: (password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE = (import.meta as any).env?.VITE_API_BASE || '/v1';

interface FetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  headers?: Record<string, string>;
  body?: string;
  credentials?: 'include' | 'same-origin' | 'omit';
}

async function fetchWithAuth(endpoint: string, options: FetchOptions = {}) {
  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  });
  
  if (!res.ok) {
    const error = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `HTTP ${res.status}`);
  }
  
  if (res.status === 204) return null;
  return res.json();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = async () => {
    try {
      const data = await fetchWithAuth('/auth/me');
      if (data?.user) {
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const login = async (email: string, password: string) => {
    await fetchWithAuth('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    await fetchUser();
  };

  const register = async (data: { email: string; password: string; firstName: string; lastName: string; referralCode?: string }) => {
    await fetchWithAuth('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    await fetchUser();
  };

  const logout = async () => {
    await fetchWithAuth('/auth/logout', { method: 'POST' });
    setUser(null);
  };

  const refresh = async () => {
    try {
      await fetchWithAuth('/auth/refresh', { method: 'POST' });
      await fetchUser();
    } catch {
      setUser(null);
    }
  };

  const forgotPassword = async (email: string) => {
    await fetchWithAuth('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  };

  const resetPassword = async (token: string, password: string) => {
    await fetchWithAuth('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  };

  const verifyEmail = async (token: string) => {
    await fetchWithAuth('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  };

  const updateProfile = async (data: { firstName?: string; lastName?: string; phone?: string }) => {
    const res = await fetchWithAuth('/auth/profile', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    if (res?.user) setUser(res.user);
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    await fetchWithAuth('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  };

  const deleteAccount = async (password: string) => {
    await fetchWithAuth('/auth/account', {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    });
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      logout,
      refresh,
      forgotPassword,
      resetPassword,
      verifyEmail,
      updateProfile,
      changePassword,
      deleteAccount,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}